import React, { useMemo } from 'react';
import { Eye, EyeOff, Search, ListTodo } from 'lucide-react';

function formatLastUpdated(lastIssueUpdatedAt) {
  if (!lastIssueUpdatedAt) return 'Sem issues sincronizadas';

  const diffMs = Date.now() - new Date(lastIssueUpdatedAt).getTime();
  const diffHours = Math.floor(diffMs / 3600000);
  if (diffHours < 1) return 'Atualizado há menos de 1h';
  if (diffHours < 24) return `Atualizado há ${diffHours}h`;

  const diffDays = Math.floor(diffHours / 24);
  return `Atualizado há ${diffDays}d`;
}

export default function ProjectManagerPanel({
  allProjects,
  projects,
  searchValue,
  onSearchChange,
  savingProjectKey,
  selectedProjectKey,
  onToggleVisibility,
  onOpenIssues,
}) {
  const visibleCount = useMemo(
    () => allProjects.filter((project) => project.is_visible).length,
    [allProjects]
  );

  const hiddenCount = allProjects.length - visibleCount;

  return (
    <div className="fade-in">
      <div className="manager-summary">
        <div className="manager-stat">
          <span className="manager-stat-value">{allProjects.length}</span>
          <span className="manager-stat-label">Projetos mapeados</span>
        </div>
        <div className="manager-stat">
          <span className="manager-stat-value">{visibleCount}</span>
          <span className="manager-stat-label">Exibidos no SuperBoard</span>
        </div>
        <div className="manager-stat">
          <span className="manager-stat-value">{hiddenCount}</span>
          <span className="manager-stat-label">Ocultos localmente</span>
        </div>
      </div>

      <div className="manager-toolbar">
        <div className="manager-search">
          <Search size={16} />
          <input
            type="text"
            value={searchValue}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Buscar por nome, chave ou cliente"
          />
        </div>
        <div className="section-subtitle">{projects.length} resultado(s)</div>
      </div>

      <div className="manager-grid">
        {projects.map((project) => {
          const isSaving = savingProjectKey === project.jira_key;
          const isSelected = selectedProjectKey === project.jira_key;

          return (
            <div
              key={project.jira_key}
              className={`card manager-card ${project.is_visible ? '' : 'is-hidden'} ${isSelected ? 'is-selected' : ''}`}
            >
              <div className="manager-card-header">
                <div>
                  <div className="project-name">{project.name}</div>
                  <div className="project-key">
                    {project.jira_key}
                    {project.client_name ? ` · ${project.client_name}` : ''}
                  </div>
                </div>
                <span className={`badge ${project.is_visible ? 'badge-low' : 'badge-medium'}`}>
                  {project.is_visible ? 'Visível' : 'Oculto'}
                </span>
              </div>

              <div className="manager-meta">
                <span>{project.category || 'Sem categoria'}</span>
                <span>{project.lead || 'Sem responsável'}</span>
                <span>{formatLastUpdated(project.last_issue_updated_at)}</span>
              </div>

              <div className="manager-counts">
                <div className="manager-count">
                  <strong>{project.total_issues}</strong>
                  <span>Total</span>
                </div>
                <div className="manager-count">
                  <strong>{project.open_issues}</strong>
                  <span>Abertas</span>
                </div>
                <div className="manager-count">
                  <strong>{project.done_issues}</strong>
                  <span>Concluídas</span>
                </div>
              </div>

              <div className="manager-actions">
                <button
                  type="button"
                  className="btn"
                  disabled={isSaving}
                  onClick={() => onToggleVisibility(project.jira_key, !project.is_visible)}
                >
                  {project.is_visible ? <EyeOff size={14} /> : <Eye size={14} />}
                  {project.is_visible ? 'Ocultar do dashboard' : 'Reexibir no dashboard'}
                </button>

                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={!project.is_visible}
                  onClick={() => onOpenIssues(project.jira_key)}
                >
                  <ListTodo size={14} />
                  Ver issues
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {projects.length === 0 && (
        <div className="card empty-state">
          Nenhum projeto encontrado com esse filtro.
        </div>
      )}
    </div>
  );
}
